import { categories, videos } from "@/mocks/videos";
import type { DurationRange, SortKey, Video, VideoCategory } from "@/types/video";

export function getCategoryBySlug(slug: string): VideoCategory | undefined {
  return categories.find((item) => item.slug === slug);
}

export function getCategoryName(slug: string): string {
  return getCategoryBySlug(slug)?.name ?? slug;
}

export function getVideoById(id: string): Video | undefined {
  return videos.find((item) => item.id === id);
}

export function getFeaturedVideos(): Video[] {
  return videos.filter((item) => item.featured).slice(0, 5);
}

export function getVideosByCategory(slug: string): Video[] {
  return videos.filter((item) => item.category === slug);
}

export function sortVideos(list: Video[], key: SortKey): Video[] {
  const sorted = [...list];
  if (key === "plays") {
    sorted.sort((a, b) => b.plays - a.plays);
  } else if (key === "oldest") {
    sorted.sort((a, b) => +new Date(a.publishDate) - +new Date(b.publishDate));
  } else {
    sorted.sort((a, b) => +new Date(b.publishDate) - +new Date(a.publishDate));
  }
  return sorted;
}

export function filterByDuration(list: Video[], range: DurationRange): Video[] {
  if (range === "short") return list.filter((item) => item.duration <= 180);
  if (range === "medium") return list.filter((item) => item.duration > 180 && item.duration <= 600);
  if (range === "long") return list.filter((item) => item.duration > 600);
  return list;
}

export function searchVideos(keyword: string): Video[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  const exact: Video[] = [];
  const fuzzy: Video[] = [];
  videos.forEach((item) => {
    const title = item.title.toLowerCase();
    if (title === kw) {
      exact.push(item);
      return;
    }
    const haystack = [item.title, item.description, item.author, getCategoryName(item.category)]
      .join(" ")
      .toLowerCase();
    if (haystack.includes(kw)) {
      fuzzy.push(item);
    }
  });
  return [...exact, ...fuzzy];
}

export function getRelatedVideos(video: Video, limit = 6): Video[] {
  const sameCategory = videos.filter((item) => item.category === video.category && item.id !== video.id);
  const byPlays = sortVideos(sameCategory, "plays");
  if (byPlays.length >= limit) return byPlays.slice(0, limit);
  const others = sortVideos(
    videos.filter((item) => item.category !== video.category && item.id !== video.id),
    "plays",
  );
  return [...byPlays, ...others].slice(0, limit);
}

export function getHotVideos(limit = 6): Video[] {
  return sortVideos(videos, "plays").slice(0, limit);
}

export function getLatestVideos(limit = 6): Video[] {
  return sortVideos(videos, "newest").slice(0, limit);
}

export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function formatPlays(plays: number): string {
  if (plays >= 10000) {
    return `${(plays / 10000).toFixed(1)}万`;
  }
  return `${plays}`;
}

export function formatDate(date: string): string {
  const d = new Date(date);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}